<template>
  <div>
    <loader v-if="leads === null" />
    <div v-else-if="leads.data.length === 0">
      Your search returned no results
    </div>

    <div v-else>
      <h3 class="font-medium text-lg mb-4">Lead Library</h3>
      <div class="flex flex-wrap items-center -mx-2 mb-6">
        <div class="w-full md:w-1/2 px-2">
          <input type="text" v-model="searchParams.q" placeholder="Search" class="input-sm w-full rounded" />
        </div>
      </div>

      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-left w-full">
          <thead class="uppercase text-xs">
            <tr>
              <th class="p-2 whitespace-no-wrap">ID</th>
              <th class="p-2">Pro</th>
              <th class="p-2 whitespace-no-wrap">Department</th>
              <th class="p-2 whitespace-no-wrap">Vendor</th>
              <th class="p-2 whitespace-no-wrap">Property</th>
              <th class="p-2 whitespace-no-wrap">Date Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="text-sm">
            <tr v-for="lead in leads.data" :key="lead.id" class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer" @click="activeModal = lead.id">
              <td class="p-2">
                {{ lead.id }}
              </td>
              <td class="p-2">
                {{ lead.professional }}
              </td>
              <td class="p-2">
                {{ lead.department }}
              </td>
              <td class="p-2">
                {{ lead.name }}
              </td>
              <td class="p-2">
                {{ lead.address }} {{ lead.postcode }}
                <small class="block text-grey-dark mt-1">
                  <span v-if="lead.bedrooms">{{ lead.bedrooms }} bed </span>{{ lead.property_type.replace('-', ' ') }}
                </small>
              </td>
              <td class="p-2">
                {{ lead.created_at | datetime }}
              </td>
              <td class="text-right">
                <button type="button" class="bg-tertiary text-white p-2 my-2 rounded">
                  More Information
                </button>
                <modal
                  v-if="activeModal === lead.id"
                  @leave="activeModal = null"
                  :showCloseBtn="true"
                >
                  <div class="text-left">
                    <h3 class="font-medium text-lg mb-4 font-bold">{{ lead.address }} {{ lead.post_code }}</h3>
                    <p>{{ lead.professional === 'Yes' ? 'Professional Valuation Requested' : 'Online Valuation Only' }} | {{ lead.department }} Valuation Request</p>
                    <p class="text-xs text-grey-dark mt-1">Lead ID: {{ lead.id }}. Received at: {{ lead.created_at | datetime }}</p>

                    <table class="w-full mt-6">
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Online Valuation</td>
                        <td class="p-2">{{ lead.valuation | gbp }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Relocation Time</td>
                        <td class="p-2">{{ lead.relocation_time }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Vendor</td>
                        <td class="p-2">{{ lead.name }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Phone Number</td>
                        <td class="p-2">{{ lead.phone }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">E-Mail Address</td>
                        <td class="p-2"><a :href="`mailto:${lead.email}`" class="text-primary">{{ lead.email }}</a></td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Property Address</td>
                        <td class="p-2">{{ lead.address }} {{ lead.post_code }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Property Type</td>
                        <td class="p-2">{{ lead.property_type.replace('-', ' ') }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Bedrooms</td>
                        <td class="p-2">{{ lead.bedrooms }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.preferred_times && lead.preferred_times.length">
                        <td class="p-2 font-bold whitespace-no-wrap">Preferred Times</td>
                        <td class="p-2">
                          <div v-for="(preferred_time, i) in lead.preferred_times" :key="i" :class="{'mb-3': i<lead.preferred_times.length - 1}">
                            <strong class="font-bold block">{{ preferred_time.date | verboseDate(false) }}</strong>
                            <div class="mt-1 text-grey-dark">
                              {{ preferred_time.times.join(', ') }}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>
                </modal>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <paginator :data="leads" @change="pageChanged" />
    </div>

    <div class="mt-6">
      <h3 class="font-medium text-lg mb-4">Download Spreadsheet</h3>

      <div class="flex flex-wrap items-end mb-4 -mx-4">
        <div class="w-full md:w-1/3 lg:w-1/4 mb-4 md:mb-0 px-4">
          <label class="label-xs">From</label>
          <date-time-picker v-model="report.from" format="d MMMM yyyy" value-format="yyyy-MM-dd" :time-picker="false" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm" :max-date="report.to"  />
        </div>

        <div class="w-full md:w-1/3 lg:w-1/4 mb-4 md:mb-0 px-4">
          <label class="label-xs">To</label>
          <date-time-picker v-model="report.to" format="d MMMM yyyy" value-format="yyyy-MM-dd" :time-picker="false" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm" :min-date="report.from"  />
        </div>

        <button
          class="bg-secondary text-white rounded mx-4 px-6 py-2 w-1/2 md:w-1/3 lg:w-1/6"
          @click="downloadReport"
        >
          Download
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import DateTimePicker from 'vue-vanilla-datetime-picker'

export default {
  components: {
    DateTimePicker
  },
  data () {
    return {
      leads: null,
      searchParams: {
        page: 1,
        q: ''
      },
      complete: 'complete',
      pageHasChanged: false,
      activeModal: null,
      report: {
        from: moment().subtract(1, 'year'),
        to: moment()
      }
    }
  },

  computed: {
    ...mapState(['selectedOffice'])
  },

  methods: {
    moment: moment,

    ...mapActions(['displayToastNotification']),

    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    },

    fetchLeads: debounce(function () {
      if (!this.selectedOffice.id) {
        return
      }

      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      this.leads = null
      api.get('evaluate/leads', {
        params: this.searchParams
      }).then((response) => {
        this.leads = response.data
      }).catch(() => {
        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-bell',
          title: 'Error',
          info: "We're unable to fetch your leads at this time."
        })
      })
    }, 500),
    downloadReport () {
      this.report.officeID = this.selectedOffice.id
      api
        .get(`evaluate/report`, {
          params: {
            officeID: this.selectedOffice.id,
            from: moment(this.report.from).format('YYYY-MM-DD'),
            to: moment(this.report.to).format('YYYY-MM-DD')
          },
          responseType: 'blob'
        })
        .then(({ data }) => {
          let blob = new Blob([data])

          if (!window.navigator.msSaveOrOpenBlob) {
            let url = window.URL.createObjectURL(blob)
            let link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'evaluate-report.xlsx')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          } else {
            window.navigator.msSaveOrOpenBlob(blob, 'evaluate-report.xlsx')
          }
        })
        .catch(() => {
          this.displayToastNotification({
            type: 'warning',
            icon: 'fa-bell',
            title: 'Download Failed',
            info: "We're unable to download the requested report at this time."
          })
        })
    }
  },

  created () {
    this.searchParams.officeID = this.selectedOffice.id
    this.fetchLeads()
  },

  watch: {
    selectedOffice () {
      this.searchParams.page = 1
      this.searchParams.officeID = this.selectedOffice.id
    },
    searchParams: {
      handler () {
        this.fetchLeads()
      },
      deep: true
    },
    complete () {
      this.searchParams.page = 1
      this.fetchLeads()
    }
  }
}
</script>
